import {ref} from 'vue';
import {googleCalendarAPI} from '../services/api';
import {getI18nT} from '../i18n';

const isConnected = ref(false);
const isLoading = ref(false);
const error = ref<string | null>(null);

export function useGoogleCalendar() {
  const fetchStatus = async (): Promise<boolean> => {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await googleCalendarAPI.getStatus();
      isConnected.value = response.connected;
      return response.connected;
    } catch (err: any) {
      console.error('Error fetching Google Calendar status:', err);
      const t = getI18nT();
      error.value = err.response?.data?.message || t('composables.calendar.error_fetch_status');
      isConnected.value = false;
      return false;
    } finally {
      isLoading.value = false;
    }
  };

  const connect = async (): Promise<void> => {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await googleCalendarAPI.getAuthUrl();
      // Redirect user to Google OAuth
      window.location.href = response.redirect_url;
    } catch (err: any) {
      console.error('Error getting Google Calendar auth URL:', err);
      const t = getI18nT();
      error.value = err.response?.data?.message || t('composables.calendar.error_connect');
      isLoading.value = false;
    }
  };

  const disconnect = async (): Promise<void> => {
    isLoading.value = true;
    error.value = null;

    try {
      await googleCalendarAPI.disconnect();
      isConnected.value = false;
    } catch (err: any) {
      console.error('Error disconnecting Google Calendar:', err);
      const t = getI18nT();
      error.value = err.response?.data?.message || t('composables.calendar.error_disconnect');
    } finally {
      isLoading.value = false;
    }
  };

  const handleCallback = async (code: string, state: string): Promise<boolean> => {
    isLoading.value = true;
    error.value = null;

    try {
      await googleCalendarAPI.handleCallback(code, state);
      await fetchStatus(); // Refresh status after successful callback
      return true;
    } catch (err: any) {
      console.error('Error handling Google Calendar callback:', err);
      const t = getI18nT();
      error.value = err.response?.data?.message || t('composables.calendar.error_callback');
      return false;
    } finally {
      isLoading.value = false;
    }
  };

  return {
    isConnected,
    isLoading,
    error,
    fetchStatus,
    connect,
    disconnect,
    handleCallback,
  };
}
